import { useState, useCallback, useEffect } from "react";
import departmentService from "../services/departmentService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import { useGeneralMessage } from "../../common/APICommon";
import { FEATURES } from "../../config/features";

export const useDepartment = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [deptRes, divRes] = await Promise.all([
        departmentService.getAll(),
        FEATURES.DIVISION_MODULE ? departmentService.getDivisions() : Promise.resolve({ data: [] })
      ]);

      setData(deptRes.data || []);
      setFilterData(deptRes.data || []);

      if (FEATURES.DIVISION_MODULE) {
        const activeDivisions = divRes.data.filter(d => d.isActive !== false);
        setDivisionOptions(activeDivisions.map(d => ({
          value: d.id,
          label: d.description
        })));
      }
    } catch (err) {
      console.error("Failed to fetch department data", err);
      showGeneralMessage("Failed to load department data", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);
      if (Array.isArray(auditData) && auditData.length > 0) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData && !Array.isArray(auditData)) {
        await SetAuditTrail(auditData);
      }
      showGeneralMessage(res.data?.message || "Action successful", true);
      await fetchData();
      return { success: true };
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "An error occurred", false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.description?.toLowerCase().includes(lower) ||
      f.id?.toString().includes(lower) ||
      (FEATURES.DEPARTMENT_CODE && f.code?.toLowerCase().includes(lower)) ||
      (FEATURES.DIVISION_MODULE && f.divisionDescription?.toLowerCase().includes(lower))
    ));
  };

  return {
    loading,
    data,
    divisionOptions,
    showMessage,
    message,
    isSuccess,
    searchData,
    createDepartment: (p, audit) => handleAction(departmentService.create, audit, p),
    updateDepartment: (id, p, audit) => handleAction(departmentService.update, audit, id, p),
    refresh: fetchData
  };
};